import React, { useEffect, useState } from 'react';
import { getVerificationList, verificationFileUrl } from '../pages/ai-interview/utils/aiInterviewApi';
import { Icon } from './reportIcons';

/**
 * Verification section for the report: identity snapshot + rejoin snapshots, the read-aloud voice check
 * (playable, with its transcript + pass state), and the network-quality result. Loaded on demand.
 */
const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }); } catch { return ''; } };

const PassBadge = ({ passed }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${passed
    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
    {passed ? 'Verified' : 'Low confidence'}
  </span>
);

const SectionCard = ({ icon, title, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
    <div className="mb-3 flex items-center gap-2 text-gray-700 dark:text-gray-200">
      <Icon name={icon} className="h-4 w-4 text-gray-400" />
      <h4 className="text-sm font-bold">{title}</h4>
    </div>
    {children}
  </div>
);

const VerificationSection = ({ companyId, sessionId, candidateId }) => {
  const ready = companyId != null && sessionId && candidateId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ready) { setLoading(false); return; }
    let alive = true;
    getVerificationList(companyId, sessionId, candidateId)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ready, companyId, sessionId, candidateId]);

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading verification…</p>;

  const photo = data?.photo;
  const rejoins = data?.rejoins || [];
  const voice = data?.voice;
  const network = data?.network;
  const nothing = !photo && rejoins.length === 0 && !voice && !network;

  if (nothing) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 text-center dark:border-gray-700">
        <Icon name="id" className="h-9 w-9 text-gray-400" />
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">No verification captured</span>
        <span className="max-w-xs text-xs text-gray-500 dark:text-gray-400">Identity, voice or network checks weren’t enabled for this interview.</span>
      </div>
    );
  }

  const Photo = ({ src, title, sub, ring }) => (
    <figure className="text-center">
      {/* Preserve the natural aspect ratio — show the whole snapshot, never crop to the face. */}
      <img alt={title} loading="lazy" src={src} className={`h-auto w-44 rounded-xl object-contain ring-1 ${ring}`} />
      <figcaption className="mt-1.5">
        <span className="block text-xs font-semibold text-gray-700 dark:text-gray-200">{title}</span>
        {sub && <span className="block text-[10px] text-gray-400">{sub}</span>}
      </figcaption>
    </figure>
  );

  return (
    <div className="space-y-4">
      {(photo || rejoins.length > 0) && (
        <SectionCard icon="id" title="Identity snapshots">
          <div className="flex flex-wrap gap-4">
            {photo && (
              <Photo src={verificationFileUrl(companyId, sessionId, candidateId, 'photo', photo.name)}
                title="Identity" sub={fmt(photo.captured_at)} ring="ring-gray-200 dark:ring-gray-700" />
            )}
            {rejoins.map((r, i) => (
              <Photo key={r.name} src={verificationFileUrl(companyId, sessionId, candidateId, 'rejoin', r.name)}
                title={`Rejoin ${i + 1}`} sub={fmt(r.captured_at)} ring="ring-blue-200 dark:ring-blue-800" />
            ))}
          </div>
        </SectionCard>
      )}

      {voice && (
        <SectionCard icon="mic" title="Voice check">
          <div className="flex flex-wrap items-center gap-3">
            <audio controls preload="none" className="h-10 w-full max-w-md"
              src={verificationFileUrl(companyId, sessionId, candidateId, 'voice', voice.name)} />
            {voice.passed != null && <PassBadge passed={voice.passed} />}
            {voice.match_ratio != null && (
              <span className="text-xs text-gray-400">match {Math.round(voice.match_ratio * 100)}%</span>
            )}
          </div>
          {voice.paragraph_text && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400"><span className="font-semibold">Passage:</span> {voice.paragraph_text}</p>
          )}
          {voice.transcript && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400"><span className="font-semibold">Heard:</span> “{voice.transcript}”</p>
          )}
        </SectionCard>
      )}

      {network && (
        <SectionCard icon="wifi" title="Network quality">
          <div className="flex flex-wrap gap-4 text-sm">
            {network.rating && <span className="font-semibold text-gray-800 dark:text-gray-200">{network.rating}</span>}
            {network.ping_ms != null && <span className="text-gray-500 dark:text-gray-400">ping {Math.round(network.ping_ms)} ms</span>}
            {network.jitter_ms != null && <span className="text-gray-500 dark:text-gray-400">jitter {Math.round(network.jitter_ms)} ms</span>}
            {network.packet_loss_pct != null && <span className="text-gray-500 dark:text-gray-400">loss {network.packet_loss_pct}%</span>}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

export default VerificationSection;
